import { useEffect, useState } from 'react'
import { LIVE_POSITION_MAX_AGE_S, type LivePosition } from '@gk/contracts'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import { usePageVisible } from '../ui/hooks.ts'
import { businessCode } from '../ui/sensitive.tsx'
import { useLocationOn } from './location.ts'

/** Who is where on the course (M19): the positions the phones of the people on a RUNNING task last shared, read from the server
 *  (`api.live_positions`) every LIVE_POLL_MS while the page is on screen and online. Memory only: nothing is written to this device, and what was
 *  shown is forgotten when the network goes (an old position would mislead). */
export const LIVE_POLL_MS = 30_000

export interface LiveRead { rows: LivePosition[] | null; error: string | null; online: boolean; loadedAt: number | null }

const num = (v: unknown): number => (typeof v === 'number' ? v : Number(v))
/** The server's rows, defensively typed (jsonb numbers may arrive as text from a numeric column). */
export function parseLive(raw: unknown): LivePosition[] {
  if (!Array.isArray(raw)) return []
  return raw.flatMap((r: Record<string, unknown> | null) => {
    if (!r || typeof r !== 'object' || typeof r.person_id !== 'string') return []
    const lat = num(r.lat), lon = num(r.lon), accuracy_m = num(r.accuracy_m)
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return []
    return [{
      person_id: r.person_id, display_name: String(r.display_name ?? ''), session_id: String(r.session_id ?? ''), job_id: (r.job_id as string | null) ?? null,
      work_order_id: (r.work_order_id as string | null) ?? null, title: (r.title as LivePosition['title']) ?? null, asset_id: (r.asset_id as string | null) ?? null,
      asset_tag: (r.asset_tag as string | null) ?? null, lat, lon, accuracy_m: Number.isFinite(accuracy_m) ? accuracy_m : 0, at: String(r.at ?? ''),
    }]
  })
}

/** One marker per person: a person on two sessions at once (an overlap, flagged on the server) is shown where their newest position puts them.
 *  Ordered by name, like the server's list. */
export function onePerPerson(rows: readonly LivePosition[]): LivePosition[] {
  const best = new Map<string, LivePosition>()
  for (const r of rows) { const b = best.get(r.person_id); if (!b || r.at > b.at) best.set(r.person_id, r) }
  return [...best.values()].sort((a, b) => a.display_name.localeCompare(b.display_name, 'fr') || a.person_id.localeCompare(b.person_id))
}

/** Rows still fresh at `now` (the server already hides older ones; a page left open must not show a position that aged on screen). */
export const freshRows = (rows: readonly LivePosition[], now: number, maxAgeS = LIVE_POSITION_MAX_AGE_S): LivePosition[] =>
  rows.filter((r) => { const at = Date.parse(r.at); return Number.isFinite(at) && now - at <= maxAgeS * 1000 })

/** "CÉ" for "Camille Équipe (fictif)": the first letters of the first and the last word of the name (what is in brackets is left out). */
export function initials(name: string): string {
  const words = name.replace(/\([^)]*\)/g, ' ').split(/\s+/).filter((w) => /\p{L}/u.test(w))
  const first = (w: string) => [...w.normalize('NFC')].find((c) => /\p{L}/u.test(c))!.toLocaleUpperCase('fr')
  if (!words.length) return '?'
  return words.length === 1 ? first(words[0]!) : first(words[0]!) + first(words[words.length - 1]!)
}

/** Whole minutes since the position was taken (0 under a minute; never negative when the phone's clock ran ahead). */
export const minutesAgo = (at: string, now: number): number => Math.max(0, Math.floor((now - Date.parse(at)) / 60_000))

/** A colour per person, stable from their id, from the theme's palette (a marker's disc). One's own marker has its own colour and ring. */
export const MARKER_TONES = ['bg-brand', 'bg-sky-700', 'bg-violet-700', 'bg-amber-700', 'bg-rose-700', 'bg-teal-700', 'bg-indigo-700', 'bg-sand-ink'] as const
export const MY_MARKER_TONE = 'bg-flag'
export function toneFor(personId: string): string {
  let h = 0
  for (const c of personId) h = (h * 31 + c.charCodeAt(0)) >>> 0
  return MARKER_TONES[h % MARKER_TONES.length]!
}

/** The title of what the person is doing: the job's (bilingual), else the workshop label (a work order). */
export const liveTitle = (r: Pick<LivePosition, 'title'>, lang: string): string | null => {
  if (!r.title) return null
  const l = lang.startsWith('en') ? 'en' : 'fr'
  return r.title[l] ?? r.title.fr ?? r.title.en ?? null
}

/** Polls `api.live_positions` while `enabled`, the page is visible and the device online, and never where location is off at the site (M21: the server
 *  shows nobody there anyway). */
export function useLivePositions(asked: boolean): LiveRead {
  const { transport, org, site } = useClub()
  const location = useLocationOn()
  const enabled = asked && location
  const online = useEngineState().online !== false
  const visible = usePageVisible()
  const [rows, setRows] = useState<LivePosition[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loadedAt, setLoadedAt] = useState<number | null>(null)
  useEffect(() => {
    if (!enabled || !online || !visible) return
    let live = true
    const load = async () => {
      try {
        const r = await transport.rpc<unknown>('live_positions', { p_org: org.org_id, p_site: site.site_id })
        if (live) { setRows(parseLive(r)); setError(null); setLoadedAt(Date.now()) }
      } catch (e) { if (live) setError(businessCode(e) ?? 'UNKNOWN') }
    }
    void load()
    const id = setInterval(() => void load(), LIVE_POLL_MS)
    return () => { live = false; clearInterval(id) }
  }, [enabled, online, visible, transport, org.org_id, site.site_id])
  useEffect(() => { if (!online || !enabled) { setRows(null); setLoadedAt(null) } }, [online, enabled])
  return { rows, error, online, loadedAt }
}
